import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PapiDeviation } from '@wix/da-papi-types';
import { getEnv } from '@wix/da-react-app/pkg/client/Environment/redux/selectors';
import { actions as streamActions, StreamType } from '@wix/da-shared-react/pkg/Stream';
import { getStreamIdsByGroupId } from '@wix/da-shared-react/pkg/Stream/base/selectors';
import { useNormalizedDeviationStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import {
  getGallectionSectionType,
  getSearchQuery,
  getShouldHideFolderSlider,
  getSortOrder,
} from '../../../../../selectors/gallectionSection';
import { getCurrentPage } from '../../../../../selectors/pageInfo';
import {
  DEVIATIONS_PER_FETCH_COUNT,
  GALLECTION_SEARCH_STREAM_GROUP_ID,
  getGallectionSearchStreamId,
} from '../../../../../streams/folderDeviations';
import { fetchNextGallectionSearchResults } from '../../../../../actions/gallectionSection';
import { GallectionType } from '../../../../../../types/gallection';

export interface UseInfiniteGallectionSearchReturn {
  currentPage: number;
  deviations: PapiDeviation[];
  errorMessage?: string;
  gallectionType: GallectionType;
  hideFolderSlider: boolean;
  isLoadingFirstResults: boolean;
  requestUrl: string;
  searchQuery: string;
  streamHasMore: boolean;
  streamId: string;
  streamTotalCount: number;
}

export default function useInfiniteGallectionSearch(): UseInfiniteGallectionSearchReturn {
  const dispatch = useDispatch();
  const searchQuery = useSelector(getSearchQuery);
  const sortOrder = useSelector(getSortOrder);
  const gallectionType = useSelector(getGallectionSectionType);
  const currentPage = useSelector(getCurrentPage);
  const hideFolderSlider = useSelector(getShouldHideFolderSlider);
  const requestUrl = useSelector(state => getEnv(state).requestUrl) ?? '';
  const streamId = useMemo(
    () =>
      getGallectionSearchStreamId({
        gallectionType,
        mode: 'scroll',
        searchQuery,
        sortOrder,
      }),
    [gallectionType, searchQuery, sortOrder]
  );
  const streamIds = useSelector(state =>
    getStreamIdsByGroupId(state, GALLECTION_SEARCH_STREAM_GROUP_ID)
  );
  const initConfig = useMemo(
    () => ({
      autoFetch: true,
      fetchNextCallback: fetchNextGallectionSearchResults,
      groupId: GALLECTION_SEARCH_STREAM_GROUP_ID,
      hasMore: true,
      itemsPerFetch: DEVIATIONS_PER_FETCH_COUNT,
      streamParams: {
        gallectionType,
        mode: 'scroll' as const,
        searchQuery,
        sortOrder,
      },
      streamType: StreamType.WITH_OFFSET,
    }),
    [gallectionType, searchQuery, sortOrder]
  );
  const stream = useNormalizedDeviationStream(streamId, 500, initConfig);

  useEffect(() => {
    streamIds
      .filter(staleStreamId => staleStreamId !== streamId)
      .forEach(staleStreamId => dispatch(streamActions.deleteStream(staleStreamId)));
  }, [dispatch, streamId, streamIds]);

  return {
    currentPage,
    deviations: stream.items,
    errorMessage: stream.errorMessage,
    gallectionType,
    hideFolderSlider,
    isLoadingFirstResults: !stream.items.length && (!stream.isInitialized || stream.isFetching),
    requestUrl,
    searchQuery,
    streamHasMore: stream.hasMore,
    streamId,
    streamTotalCount: stream.total || 0,
  };
}
